import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';

// Mock preference data - structure represents user preferences across categories
const generateMockPreferenceData = () => {
  const categories = ['Electronics', 'Clothing', 'Books', 'Home & Garden', 'Sports', 'Beauty', 'Food', 'Art'];
  const subcategories = {
    'Electronics': ['Smartphones', 'Laptops', 'Headphones', 'Smart Home', 'Gaming'],
    'Clothing': ['Casual', 'Formal', 'Athletic', 'Accessories', 'Footwear'],
    'Books': ['Fiction', 'Non-Fiction', 'Biographies', 'Technical', 'Self-Help'],
    'Home & Garden': ['Kitchen', 'Bathroom', 'Bedding', 'Furniture', 'Tools'],
    'Sports': ['Cardio', 'Strength', 'Water Sports', 'Winter', 'Team Sports'],
    'Beauty': ['Skincare', 'Hair Care', 'Makeup', 'Fragrance', 'Nails'],
    'Food': ['Organic', 'Gluten-Free', 'Low-Carb', 'Vegan', 'Gourmet'],
    'Art': ['Painting', 'Sculpture', 'Digital', 'Photography', 'Crafts']
  };

  const data = [];
  categories.forEach(category => {
    subcategories[category].forEach(subcategory => {
      // Generate preference scores 0-10
      const preference = Math.floor(Math.random() * 11);
      data.push({
        category,
        subcategory,
        preference,
        formattedName: `${subcategory}`,
        color: d3.interpolateRdYlBu(1 - preference / 10) // Cold for low preference, warm for high
      });
    });
  });

  return data;
};

const PreferenceHeatmap = ({ width = 800, height = 600 }) => {
  const svgRef = useRef();
  const tooltipRef = useRef();

  useEffect(() => {
    const data = generateMockPreferenceData();

    // Clear previous content
    d3.select(svgRef.current).selectAll("*").remove();

    const margin = { top: 80, right: 25, bottom: 30, left: 100 };
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;

    const svg = d3.select(svgRef.current)
      .attr('width', width)
      .attr('height', height)
      .append('g')
      .attr('transform', `translate(${margin.left}, ${margin.top})`);

    // Get unique categories and subcategories
    const categories = [...new Set(data.map(d => d.category))];
    const subcategories = [...new Set(data.map(d => d.subcategory))];

    // Create scales
    const xScale = d3.scaleBand()
      .domain(categories)
      .range([0, innerWidth])
      .padding(0.05);

    const yScale = d3.scaleBand()
      .domain(subcategories)
      .range([0, innerHeight])
      .padding(0.05);

    const colorScale = d3.scaleSequential(d3.interpolateRdYlBu)
      .domain([0, 10]);

    // Create tooltip
    const tooltip = d3.select(tooltipRef.current)
      .style('opacity', 0)
      .style('position', 'absolute')
      .style('pointer-events', 'none')
      .style('background', 'rgba(0, 0, 0, 0.8)')
      .style('color', 'white')
      .style('border-radius', '4px')
      .style('padding', '8px')
      .style('font-size', '12px')
      .style('z-index', '1000');

    // Create cells
    svg.selectAll()
      .data(data)
      .enter()
      .append('rect')
      .attr('x', d => xScale(d.category))
      .attr('y', d => yScale(d.subcategory))
      .attr('width', xScale.bandwidth())
      .attr('height', yScale.bandwidth())
      .attr('fill', d => colorScale(d.preference))
      .attr('stroke', '#fff')
      .attr('stroke-width', 1)
      .style('cursor', 'pointer')
      .on('mouseover', function(event, d) {
        d3.select(this)
          .attr('stroke', '#333')
          .attr('stroke-width', 2);

        tooltip.transition()
          .duration(200)
          .style('opacity', .9);
        tooltip.html(`
          <strong>${d.subcategory}</strong><br/>
          <small>Category: ${d.category}</small><br/>
          Preference: ${d.preference}/10 ⭐
        `)
        .style('left', (event.pageX + 10) + 'px')
        .style('top', (event.pageY - 10) + 'px');
      })
      .on('mousemove', function(event) {
        tooltip.style('left', (event.pageX + 10) + 'px')
              .style('top', (event.pageY - 10) + 'px');
      })
      .on('mouseout', function() {
        d3.select(this)
          .attr('stroke', '#fff')
          .attr('stroke-width', 1);

        tooltip.transition()
          .duration(500)
          .style('opacity', 0);
      });

    // Add axes
    const xAxis = d3.axisTop(xScale)
      .tickSize(0)
      .tickPadding(10);

    const yAxis = d3.axisLeft(yScale)
      .tickSize(0)
      .tickPadding(10);

    svg.append('g')
      .call(xAxis)
      .attr('transform', `translate(0, -10)`)
      .selectAll('text')
      .style('text-anchor', 'start')
      .style('font-weight', 'bold')
      .style('font-size', '11px')
      .attr('dy', '0.35em')
      .attr('transform', 'rotate(-45) translate(-10, 0)');

    svg.append('g')
      .call(yAxis)
      .attr('transform', `translate(-10, 0)`)
      .selectAll('text')
      .style('font-size', '10px')
      .style('text-anchor', 'end');

    // Add title
    svg.append('text')
      .attr('x', innerWidth / 2)
      .attr('y', -40)
      .attr('text-anchor', 'middle')
      .style('font-size', '18px')
      .style('font-weight', 'bold')
      .style('fill', '#667eea')
      .text('Preference Heatmap');

    svg.append('text')
      .attr('x', innerWidth / 2)
      .attr('y', -20)
      .attr('text-anchor', 'middle')
      .style('font-size', '12px')
      .style('fill', '#666')
      .text('🎯 Hover to see preference scores | Cold (Low) → Warm (High)');

    // Add legend
    const legendWidth = 200;
    const legendHeight = 20;

    const legendScale = d3.scaleLinear()
      .domain([0, 10])
      .range([0, legendWidth]);

    const legend = svg.append('g')
      .attr('transform', `translate(${innerWidth - legendWidth}, ${innerHeight + 20})`);

    const legendAxis = d3.axisBottom(legendScale)
      .ticks(5)
      .tickFormat(d => d);

    legend.append('defs')
      .append('linearGradient')
      .attr('id', 'legend-gradient')
      .attr('x1', '0%')
      .attr('x2', '100%')
      .attr('y1', '0%')
      .attr('y2', '0%')
      .selectAll('stop')
      .data(d3.range(0, 1.1, 0.1))
      .enter()
      .append('stop')
      .attr('offset', d => d)
      .attr('stop-color', d => d3.interpolateRdYlBu(1 - d));

    legend.append('rect')
      .attr('width', legendWidth)
      .attr('height', legendHeight)
      .style('fill', 'url(#legend-gradient)')
      .attr('stroke', '#333')
      .attr('stroke-width', 1);

    legend.append('g')
      .attr('transform', `translate(0, ${legendHeight})`)
      .call(legendAxis)
      .selectAll('text')
      .style('font-size', '10px');

    legend.append('text')
      .attr('x', legendWidth / 2)
      .attr('y', legendHeight + 30)
      .attr('text-anchor', 'middle')
      .style('font-size', '11px')
      .style('font-weight', 'bold')
      .style('fill', '#333')
      .text('Preference Score');

  }, [width, height]);

  return (
    <div style={{ position: 'relative', textAlign: 'center' }}>
      <svg ref={svgRef}></svg>
      <div ref={tooltipRef}></div>
    </div>
  );
};

export default PreferenceHeatmap;
